/**
 * graphql.client.ts — the ONE GraphQL transport module (B1.3'; fullsuite plan
 * §B1', fe-client gapfill research 2026-08-08 §3.1).
 *
 * Stack (research pick): `@urql/core` (core only — NO React bindings, NO
 * normalized cache; Zustand + ahooks stay the state/cache owner) +
 * `@urql/exchange-persisted` + the codegen client-preset trusted-documents
 * manifest. No component ever imports this module — GraphQL execution lives
 * behind the `src/services/api/*` facades (Strangler-Fig seam; the CU flips
 * swap transports INSIDE an adapter, zero hook call-sites change).
 *
 * Persisted-documents posture (incremental adoption, by construction):
 * `generateHash` returns the codegen-stamped `__meta__.hash` when the
 * document came from the client preset, and `null` otherwise — and a `null`
 * hash makes the persisted exchange SKIP that operation entirely (documented
 * exchange contract), so interim raw-text documents (see
 * graphql.operations.ts) ride plain POSTs with the full query text. As
 * codegen adopts a module's operations (codegen.ts `adoptedOperations`, at
 * its CU flip), its documents start carrying hashes; the server-side
 * allowlist (`@graphql-yoga/plugin-persisted-operations` over
 * `persisted-documents.json`) is enforced at B7.2'. Between adoption and
 * B7.2' the server must keep arbitrary operations allowed in dev — recorded
 * in the B7.2' slice, not here.
 *
 * Error contract: axios callers throw on non-2xx; GraphQL returns 200 with an
 * `errors` array. `gqlRequest` converts BOTH failure kinds into ONE typed
 * `GraphQLTransportError` so facade catch-blocks and ahooks `onError` keep a
 * single shape (house api-error-handling posture; budgeted once, here).
 *
 * URL: relative `/graphql` — the vite proxy targets the GraphQL server in
 * BOTH modes (MSW has no /graphql handlers and its worker bypasses unhandled
 * non-/api paths), so the session cookie rides same-origin with zero
 * CORS/credentials config (host-layer gapfill research §3.1). Seam ports
 * never appear here (seam-audit invariant 2).
 */

import { Client, fetchExchange, type AnyVariables, type CombinedError } from '@urql/core';
import { persistedExchange } from '@urql/exchange-persisted';
import type { DocumentInput } from '@urql/core';

/** The client-preset stamps its trusted-document hash here (codegen.ts). */
interface PersistedDocumentMeta {
  __meta__?: {
    hash?: string;
  };
}

/**
 * One expected shape for a GraphQL transport failure — network-level or
 * GraphQL-level — thrown by `gqlRequest` so every facade handles one error
 * type. `status` is present only for HTTP-level failures.
 */
export class GraphQLTransportError extends Error {
  readonly graphQLErrors: readonly string[];
  readonly networkError: boolean;
  readonly status?: number;

  constructor(options: {
    message: string;
    graphQLErrors?: readonly string[];
    networkError?: boolean;
    status?: number;
  }) {
    super(options.message);
    this.name = 'GraphQLTransportError';
    this.graphQLErrors = options.graphQLErrors ?? [];
    this.networkError = options.networkError ?? false;
    this.status = options.status;
  }
}

/** Normalize urql's CombinedError into the one transport error shape. */
export function toTransportError(error: CombinedError): GraphQLTransportError {
  const graphQLErrors = error.graphQLErrors.map((gqlError) => gqlError.message);
  const status =
    error.response && typeof (error.response as Response).status === 'number'
      ? (error.response as Response).status
      : undefined;
  return new GraphQLTransportError({
    message: error.message,
    graphQLErrors,
    networkError: Boolean(error.networkError),
    status,
  });
}

/**
 * The single urql client. Exchange order: persisted (hash stamping / skip on
 * null) → fetch. Deliberately NO cache exchange — every facade call hits the
 * network; ahooks `cacheKey`/SWR remains the only freshness owner
 * (fe-client research §1.2: a second implicit cache is a staleness bug
 * factory behind a plain-object facade).
 */
export const gqlClient = new Client({
  url: '/graphql',
  exchanges: [
    persistedExchange({
      // Trusted documents, not APQ: hashes come from the codegen manifest at
      // build time; a null hash = not a persisted operation → full text POST.
      generateHash: (_query, document) =>
        Promise.resolve((document as PersistedDocumentMeta).__meta__?.hash ?? null),
      // Mutations join the persisted flow the moment their documents carry
      // hashes (same adoption gate as queries).
      enableForMutation: true,
    }),
    fetchExchange,
  ],
  fetchOptions: { credentials: 'same-origin' },
});

/**
 * One-shot promise query — the facade-facing call. Throws
 * `GraphQLTransportError` on any failure; resolves with `data` otherwise.
 */
export async function gqlRequest<TData, TVariables extends AnyVariables = AnyVariables>(
  document: DocumentInput<TData, TVariables>,
  variables: TVariables
): Promise<TData> {
  const result = await gqlClient.query<TData, TVariables>(document, variables).toPromise();
  if (result.error) {
    throw toTransportError(result.error);
  }
  if (result.data == null) {
    throw new GraphQLTransportError({
      message: 'GraphQL response carried neither data nor errors',
      networkError: false,
    });
  }
  return result.data;
}

/**
 * One-shot promise mutation — gqlRequest's write twin (CU-1a masters flip;
 * urql derives the operation kind from the document, so mutation documents
 * must ride `client.mutation`). Same error contract as gqlRequest. Typed
 * userError handling (payload.userErrors → thrown transport error) stays in
 * the facades — the transport layer never interprets domain payloads.
 */
/**
 * Typed userError guard (CU-1a): mutation payloads carry domain failures as
 * `userErrors` rows, never top-level GraphQL errors. Facades call this on
 * every payload; a non-empty list becomes the one transport error shape
 * (codes ride `graphQLErrors` — the FE i18n `errors.*` keys), status 422.
 */
export function throwOnUserErrors(
  userErrors: ReadonlyArray<{ code: string; field: string | null; message: string }>,
  operation: string
): void {
  if (userErrors.length === 0) return;
  throw new GraphQLTransportError({
    message: `${operation}: ${userErrors.map((error) => error.message).join('; ')}`,
    graphQLErrors: userErrors.map((error) => error.code),
    status: 422,
  });
}

export async function gqlMutate<TData, TVariables extends AnyVariables = AnyVariables>(
  document: DocumentInput<TData, TVariables>,
  variables: TVariables
): Promise<TData> {
  const result = await gqlClient.mutation<TData, TVariables>(document, variables).toPromise();
  if (result.error) {
    throw toTransportError(result.error);
  }
  if (result.data == null) {
    throw new GraphQLTransportError({
      message: 'GraphQL response carried neither data nor errors',
      networkError: false,
    });
  }
  return result.data;
}
